import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { api } from '../api/client';
import { useAnalyses } from '../analyses/AnalysesContext';
import { useAuth } from '../auth/AuthContext';

function formatProcessing(seconds) {
  if (seconds === null || seconds === undefined) return '';
  if (seconds < 60) return `${Math.round(seconds)} s`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')} min`;
}


function RelatedAnalyses({ primary }) {
  const [items, setItems] = useState([]);
  const [models, setModels] = useState([]);
  const [picking, setPicking] = useState(false);
  const [newModel, setNewModel] = useState('');
  const [busy, setBusy] = useState(false);

  // Audio-File-Identität als Schwester-Schlüssel: filename + size + duration.
  // Gleiche Logik wie im DashboardPage – findet auch verwaiste Secondaries
  // (source_analysis_id=NULL nach Primary-Delete) anhand der Audio-Datei.
  const audioKey = primary.original_filename + '|' + primary.audio_size_bytes
    + '|' + (primary.audio_duration_seconds || 0);

  const load = () => {
    api.get('/analyses').then((r) => {
      const byId = new Map();
      for (const a of r.data) byId.set(a.id, a);
      // Liste enthält ALLE Analysen dieses Audios – auch die aktuell geöffnete,
      // damit der User von einer benachbarten zurück navigieren kann.
      const related = r.data.filter((x) => {
        const ref = (x.source_analysis_id && byId.get(x.source_analysis_id)) || x;
        const xKey = ref.original_filename + '|' + ref.audio_size_bytes
          + '|' + (ref.audio_duration_seconds || 0);
        return xKey === audioKey;
      });
      related.sort((x, y) => {
        const xm = (x.model_used || x.model_requested || '').toLowerCase();
        const ym = (y.model_used || y.model_requested || '').toLowerCase();
        return xm.localeCompare(ym);
      });
      setItems(related);
    });
  };
  useEffect(() => {
    load();
    api.get('/models').then((r) => setModels(r.data.models));
  }, [audioKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const onReanalyze = async () => {
    if (!newModel) return;
    setBusy(true);
    try {
      await api.post(`/analyses/${primary.id}/reanalyze`, { model: newModel });
      setPicking(false);
      setNewModel('');
      load();
    } finally {
      setBusy(false);
    }
  };

  const usedModels = new Set(items.map((i) => i.model_used || i.model_requested));
  const available = models.filter((m) => !usedModels.has(m.id));

  return (
    <div className="related-analyses">
      <h3>Analysen dieses Gesprächs</h3>
      {items.length === 0 ? (
        <p className="meta">Keine Analysen.</p>
      ) : (
        <ul>
          {items.map((x) => {
            const isCurrent = x.id === primary.id;
            return (
              <li key={x.id} className={isCurrent ? 'current-analysis' : ''}>
                {x.model_used || x.model_requested}
                {' · '}
                <span className={`status-${x.status}`}>{x.status}</span>
                {isCurrent && <span className="meta"> · aktuell geöffnet</span>}
                {!isCurrent && x.status === 'completed' && (
                  <> · <Link to={`/analyses/${x.id}`}>öffnen</Link></>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {available.length > 0 && (
        !picking ? (
          <button onClick={() => setPicking(true)} className="btn-primary-sm">
            + Weitere Analyse starten
          </button>
        ) : (
          <div className="reanalyze-picker">
            <select value={newModel} onChange={(e) => setNewModel(e.target.value)} disabled={busy}>
              <option value="">– Modell wählen –</option>
              {available.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label}
                </option>
              ))}
            </select>
            <button onClick={onReanalyze} disabled={!newModel || busy} className="btn-primary-sm">
              {busy ? '…' : 'Starten'}
            </button>
            <button onClick={() => setPicking(false)} disabled={busy}>Abbrechen</button>
          </div>
        )
      )}
    </div>
  );
}

export default function AnalysisDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { remove } = useAnalyses();
  const { user } = useAuth();
  const canChooseModel = !!user?.can_choose_model;
  const [a, setA] = useState(null);
  const [error, setError] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [loadingAudio, setLoadingAudio] = useState(false);
  const objectUrlRef = useRef(null);

  useEffect(() => {
    api
      .get(`/analyses/${id}`)
      .then((r) => setA(r.data))
      .catch((e) => setError(e.response?.status === 404 ? 'Nicht gefunden' : 'Fehler beim Laden'));
  }, [id]);

  useEffect(() => () => {
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
  }, []);

  if (error) return <div className="detail"><Link to="/">← Zurück</Link><p>{error}</p></div>;
  if (!a) return <div className="detail">lade …</div>;

  const onDelete = async () => {
    if (!window.confirm('Analyse wirklich löschen?')) return;
    await remove(a.id);
    nav('/');
  };

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(a.html_content || '');
    } catch (_) {
      window.alert('Kopieren fehlgeschlagen (Browser verweigert).');
    }
  };

  const onPlay = async () => {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      objectUrlRef.current = null;
      setAudioUrl(null);
      return;
    }
    setLoadingAudio(true);
    try {
      const res = await api.get(`/analyses/${a.id}/audio`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      objectUrlRef.current = url;
      setAudioUrl(url);
    } catch (e) {
      window.alert(e.response?.status === 404 ? 'Audio-Datei nicht verfügbar.' : 'Laden fehlgeschlagen.');
    } finally {
      setLoadingAudio(false);
    }
  };

  const created = a.created_at ? new Date(a.created_at).toLocaleString('de-DE') : '';
  const headline = a.visit_title || a.original_filename;

  return (
    <div className="detail">
      <Link to="/">← Zurück</Link>
      <h1>{headline}</h1>
      <div className="meta">
        {created}
        {canChooseModel && ` · ${a.model_used || a.model_requested}`}
        {canChooseModel && a.processing_duration_seconds != null && ` · ${formatProcessing(a.processing_duration_seconds)} Verarbeitung`}
        {a.visit_title && ` · ${a.original_filename}`}
        {a.status === 'failed' && (
          <span className="failed">
            {' '}· ✗ {canChooseModel ? a.error_message : 'Analyse fehlgeschlagen – bitte erneut versuchen'}
          </span>
        )}
      </div>
      <div className="actions">
        <button onClick={onPlay} disabled={loadingAudio}>
          {loadingAudio ? '…' : audioUrl ? '⏸ Player schließen' : '▶ Abspielen'}
        </button>
        <button onClick={onCopy}>Kopieren</button>
        <button onClick={() => window.print()}>Drucken / PDF</button>
        <button onClick={onDelete} className="danger">Löschen</button>
      </div>

      {canChooseModel && <RelatedAnalyses primary={a} />}

      {audioUrl && (
        <audio controls autoPlay src={audioUrl} className="detail-audio" />
      )}

      {a.transcript_content && (
        <details className="transcript-details">
          <summary>📝 Transkript anzeigen</summary>
          <pre className="transcript">{a.transcript_content}</pre>
        </details>
      )}

      {a.html_content ? (
        <article dangerouslySetInnerHTML={{ __html: a.html_content }} />
      ) : (
        <article><em>Noch kein Inhalt.</em></article>
      )}
    </div>
  );
}
